import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";

/**
 * Emergent Google-Auth return page.
 *
 * The flow:
 *  1. User clicks "Continue with Google" on /login → redirects to
 *     https://auth.emergentagent.com/?redirect=<origin>/auth-callback
 *  2. Emergent handles Google OAuth and returns them here with
 *     `#session_id=xxx` in the URL fragment.
 *  3. We POST that session_id to our backend, which exchanges it with
 *     Emergent server-to-server and returns a normal app JWT.
 *  4. Save JWT + user to AuthContext, then redirect home.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
 * URLS, THIS BREAKS THE AUTH.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setAuthFromToken } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    // useRef, set synchronously — prevents double-execution under StrictMode
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      toast.error("No session id from Google — please try again");
      navigate("/login", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google-callback`, { session_id });
        // Route through AuthContext so header / protected pages pick up the change
        setAuthFromToken(res.data.access_token, res.data.user);
        toast.success(`Welcome, ${res.data.user.full_name || res.data.user.email}!`);
        // Clean the hash so a reload doesn't try to exchange again
        window.history.replaceState({}, "", "/");
        navigate("/", { replace: true });
      } catch (err) {
        toast.error(err.response?.data?.detail || "Google sign-in failed");
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setAuthFromToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#7C2C3B] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="mt-4 text-slate-600 text-sm">Completing sign-in…</p>
      </div>
    </div>
  );
}
